/**
 * "Tonight in the building" (V9 care-ops.js, Residents roster): one row of chips (Float cards,
 * First 72h, Back from hospital, Skin, Infection, Family notes, Fatigue), one detail open at a
 * time below the row, amber on a chip that needs action.
 *
 * Only First 72h has a back end today: GET /residents/watch/movement already says when each
 * resident was admitted. The rest come from feeds that are not connected yet (the building's
 * record system for float cards, hospital returns, skin, infection and family day notes, Swift
 * Medical for wounds, the shift roster for hours worked). Each is typed here as the section
 * its feed will answer, written by the server in the viewer's language as the medicine watch
 * is; until a feed is wired its detail says so plainly. Nothing is invented or shown as real,
 * and the design's sample residents never appear.
 */
import type { WatchRow } from '@aithora/shared';
import { pre } from './dom';
import type { ResidentsPersona } from './common';

export type TonightKey = 'cards' | '72' | 'bh' | 'skin' | 'inf' | 'fam' | 'fat';

/** One line of a detail: what, and its state. */
export interface TonightItem {
  k: string;
  v: string;
  tone: 'warn' | 'ok' | null;
}

/** A float or agency nurse's card for one resident (the record system's face sheet). */
export interface FloatCard {
  name: string;
  room: string | null;
  rows: Array<{ k: string; v: string }>;
  readByMe: boolean;
}

/** One section as its feed will answer it: the chip, the line under the head, the details. */
export interface TonightSection {
  chip: string;
  line: string;
  /** Something needs action (the chip turns amber). */
  hot: boolean;
  items: TonightItem[];
  cards?: FloatCard[];
  para?: string | null;
}

/** The feeds behind the chips, other than First 72h. null: not connected yet. */
export type TonightFeeds = Record<Exclude<TonightKey, '72'>, TonightSection | null>;

/** Every feed but First 72h waits on a connection: the honest placeholder until each is wired. */
export const TONIGHT_NOT_CONNECTED: TonightFeeds = { cards: null, bh: null, skin: null, inf: null, fam: null, fat: null };

/** How long the first-72-hours watch lasts. */
export const FIRST_HOURS = 72;

export interface TonightInput {
  persona: ResidentsPersona;
  /** The Owner/Operator has no Tonight row (V9 coVals: care staff, the admin and the Aithora team). */
  shown: boolean;
  rows: WatchRow[];
  feeds: TonightFeeds;
  nowMs: number;
  open: TonightKey | null;
  setOpen: (k: TonightKey | null) => void;
}

export const NOT_CONNECTED = 'Not connected yet. Nothing is shown here until it is.';

/** Where each section's facts will come from (shown under its detail). */
const SOURCE: Record<TonightKey, string> = {
  cards: 'Cards come from the building’s record system: how to recognize each resident, how they take pills, what calms them and family to call.',
  '72': 'The 72-hour checklist and night fall map fill in once the building’s record system is connected.',
  bh: 'Hospital returns come from discharge summaries in the building’s record system. Changes are written back there.',
  skin: 'Aithora flags who needs a look; it does not grade wounds. Measurements and photos come from Swift Medical.',
  inf: 'Small changes seen together and the antibiotic tracker fill in once charting is connected.',
  fam: 'Day notes for families are built from what staff already chart, once charting is connected.',
  fat: 'Hours worked come from the shift roster once it is connected.',
};

const HEAD: Record<TonightKey, string> = {
  cards: 'Resident cards for float and agency nurses',
  '72': 'First 72 hours',
  bh: 'Back from hospital · 30-day watch',
  skin: 'Skin watch',
  inf: 'Infection early warning',
  fam: 'Family day notes',
  fat: 'Fatigue',
};

const CHIP: Record<TonightKey, string> = {
  cards: 'Float cards', '72': 'First 72h', bh: 'Back from hospital', skin: 'Skin', inf: 'Infection', fam: 'Family notes', fat: 'Fatigue',
};

export function tonightVals(inp: TonightInput) {
  if (!inp.shown) return { opShow: false, opRows: [] as unknown[] };
  const care = inp.persona === 'Care Staff';
  const it = (x: TonightItem) => ({ k: x.k, v: x.v, warn: x.tone === 'warn', ok: x.tone === 'ok', plain: !x.tone, hasAct: false, act: null, actLabel: '' });
  const row = (key: TonightKey, s: TonightSection | null) => {
    const open = inp.open === key;
    const hot = !!s?.hot;
    const cards = s?.cards ?? [];
    return {
      key,
      chip: s ? s.chip : CHIP[key],
      head: HEAD[key],
      line: s ? s.line : NOT_CONNECTED,
      hot,
      open,
      tog: (e?: { preventDefault?: () => void }) => { pre(e); inp.setOpen(open ? null : key); },
      cOpen: open,
      cHot: !open && hot,
      cCool: !open && !hot,
      items: (s?.items ?? []).map(it),
      hasSlots: false,
      slots: [],
      hasCards: cards.length > 0,
      cards: cards.map((c) => ({ ini: c.name.split(' ').map((p) => p[0]).join('').slice(0, 2), name: c.name + (c.room ? ' · Rm ' + c.room : ''), rows: c.rows, read: c.readByMe, unread: false })),
      hasPara: !!s?.para,
      para: s?.para ?? '',
      hasBtn: false,
      btnDone: false,
      hasNote: true,
      note: SOURCE[key],
    };
  };

  // First 72 hours: residents admitted in the last 72 hours, from the watch list's admittedAt.
  const fresh = inp.rows
    .filter((r) => !r.masked && r.admittedAt && inp.nowMs - Date.parse(r.admittedAt) >= 0 && inp.nowMs - Date.parse(r.admittedAt) < FIRST_HOURS * 3600000)
    .map((r) => ({ r, hour: Math.max(1, Math.floor((inp.nowMs - Date.parse(r.admittedAt!)) / 3600000)) }))
    .sort((a, b) => a.hour - b.hour);
  const first72 = row('72', {
    chip: fresh.length ? 'First 72h · ' + fresh.length : 'First 72h',
    line: fresh.length ? fresh.length + (fresh.length === 1 ? ' resident is' : ' residents are') + ' in their first 72 hours' : 'No resident is in their first 72 hours.',
    hot: false,
    items: fresh.map((x) => ({ k: (x.r.name ?? 'Resident') + (x.r.room ? ' · Rm ' + x.r.room : ''), v: 'Hour ' + x.hour + ' of 72', tone: null })),
  });

  const f = inp.feeds;
  const rows = [row('cards', f.cards), first72, row('bh', f.bh), row('skin', f.skin), row('inf', f.inf)];
  if (!care) rows.push(row('fam', f.fam), row('fat', f.fat));
  return { opShow: true, opHead: 'Tonight in the building', opRows: rows };
}
